// ─────────────────────────────────────────────────────────────────────────────
// ImportKnowledgeBaseDialogComponent
// ─────────────────────────────────────────────────────────────────────────────
// 2-step import dialog (Upload → Preview) reusable for aliases, external
// references, and preferred products.
// Accepts { type: 'alias' | 'external-reference' | 'preferred-product' } via MAT_DIALOG_DATA.
// Step 1: template download buttons + drag-and-drop file zone (CSV-only for
// preferred products).
// Step 2: dry-run summary cards + row table with status badges; preferred
// products additionally show an overwrite warning (existing pairs are replaced).
// Closes with true on successful confirm import.
// ─────────────────────────────────────────────────────────────────────────────

import { Component, Inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { SharedModule } from 'src/app/shared/shared.module';
import { UiModule } from 'src/app/ui/ui.module';
import { CommonService } from 'src/app/shared/common.service';
import { environment } from 'src/environments/environment';
import { AliasService } from '../../services/alias.service';
import { ExternalReferenceService } from '../../services/external-reference.service';
import { PreferredProductService } from '../../services/preferred-product.service';
import { IImportPreviewResponse, IImportRow } from '../../models/import.models';

export interface ImportDialogConfig {
  type: 'alias' | 'external-reference' | 'preferred-product';
}

type ImportStep = 'upload' | 'preview';
type BadgeType = 'ok' | 'warning' | 'duplicate' | 'error';

@Component({
  selector: 'app-import-knowledge-base-dialog',
  standalone: true,
  imports: [CommonModule, TranslateModule, MatProgressSpinnerModule, SharedModule, UiModule],
  templateUrl: './import-knowledge-base-dialog.component.html',
  styleUrls: ['./import-knowledge-base-dialog.component.scss'],
})
export class ImportKnowledgeBaseDialogComponent implements OnDestroy {
  // ── Dialog config ─────────────────────────────────────────────────────────

  readonly type: 'alias' | 'external-reference' | 'preferred-product';

  // ── Step state ────────────────────────────────────────────────────────────

  currentStep: ImportStep = 'upload';

  // ── Upload step ───────────────────────────────────────────────────────────

  selectedFile: File | null = null;
  isDragOver = false;
  fileError = '';

  // ── Preview step ──────────────────────────────────────────────────────────

  previewData: IImportPreviewResponse | null = null;
  isLoadingPreview = false;
  isConfirming = false;

  // ── Cleanup ───────────────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();

  // ── Allowed file types (CSV + Excel for all KB imports) ──────────────────

  private get acceptedTypes(): string[] {
    return ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
  }

  private get acceptedExtensions(): string[] {
    return ['.csv', '.xls', '.xlsx'];
  }

  get acceptAttr(): string {
    return this.acceptedExtensions.join(',');
  }

  constructor(
    public dialogRef: MatDialogRef<ImportKnowledgeBaseDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private config: ImportDialogConfig,
    private aliasService: AliasService,
    private externalReferenceService: ExternalReferenceService,
    private preferredProductService: PreferredProductService,
    private commonService: CommonService,
    private translateService: TranslateService,
  ) {
    this.type = config.type;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Computed helpers ──────────────────────────────────────────────────────

  get titleKey(): string {
    switch (this.type) {
      case 'alias':
        return 'knowledge_base.import.aliases.title';
      case 'preferred-product':
        return 'knowledge_base.import.preferred_products.title';
      default:
        return 'knowledge_base.import.external_references.title';
    }
  }

  get infoKey(): string {
    switch (this.type) {
      case 'alias':
        return 'knowledge_base.import.aliases.info';
      case 'preferred-product':
        return 'knowledge_base.import.preferred_products.info';
      default:
        return 'knowledge_base.import.external_references.info';
    }
  }

  private get apiSegment(): string {
    switch (this.type) {
      case 'alias':
        return 'ai/kb/aliases';
      case 'preferred-product':
        return 'ai/kb/preferred-products';
      default:
        return 'ai/kb/external-references';
    }
  }

  get csvTemplateUrl(): string {
    return `${environment.BASE_URL_API}${this.apiSegment}/import/template?format=csv`;
  }

  get xlsxTemplateUrl(): string {
    return `${environment.BASE_URL_API}${this.apiSegment}/import/template?format=xlsx`;
  }

  get isAliasType(): boolean {
    return this.type === 'alias';
  }

  get isPreferredProductType(): boolean {
    return this.type === 'preferred-product';
  }

  get totalRows(): number {
    return this.previewData?.summary.rows_processed ?? 0;
  }

  get validCount(): number {
    return this.previewData?.summary.valid ?? 0;
  }

  get warningCount(): number {
    return this.previewData?.summary.warning ?? 0;
  }

  get duplicates(): number {
    return this.previewData?.summary.skipped ?? 0;
  }

  get errors(): number {
    return this.previewData?.summary.invalid ?? 0;
  }

  // ── Template download ─────────────────────────────────────────────────────

  downloadTemplate(format: 'csv' | 'xlsx'): void {
    const url = format === 'csv' ? this.csvTemplateUrl : this.xlsxTemplateUrl;
    window.open(url, '_blank');
  }

  // ── File handling ─────────────────────────────────────────────────────────

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.handleFile(file);
    }
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.handleFile(file);
    }
    input.value = '';
  }

  private handleFile(file: File): void {
    this.fileError = '';
    if (!this.isValidFileType(file)) {
      this.fileError = this.translateService.instant('knowledge_base.import.invalid_file_type');
      this.selectedFile = null;
      return;
    }
    this.selectedFile = file;
  }

  private isValidFileType(file: File): boolean {
    const name = file.name.toLowerCase();
    return (
      this.acceptedExtensions.some((ext) => name.endsWith(ext)) ||
      (this.acceptedTypes.includes(file.type) && !name.endsWith('.xls') && !name.endsWith('.xlsx'))
    );
  }

  // ── Step navigation ───────────────────────────────────────────────────────

  proceed(): void {
    if (!this.selectedFile) return;
    this.isLoadingPreview = true;

    const formData = new FormData();
    formData.append('ufile', this.selectedFile);

    const import$ = this.dispatchImport(formData, true);

    import$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.previewData = response.object;
        this.isLoadingPreview = false;
        this.currentStep = 'preview';
      },
      error: () => {
        this.isLoadingPreview = false;
      },
    });
  }

  // ── Confirm import ────────────────────────────────────────────────────────

  confirmImport(): void {
    if (!this.selectedFile || this.isConfirming) return;
    this.isConfirming = true;

    const formData = new FormData();
    formData.append('ufile', this.selectedFile);

    const import$ = this.dispatchImport(formData, false);

    import$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.isConfirming = false;
        this.commonService.showToast(this.translateService.instant('knowledge_base.import.toast.import_success'), 'SUCCESS');
        this.dialogRef.close(true);
      },
      error: () => {
        this.isConfirming = false;
      },
    });
  }

  private dispatchImport(formData: FormData, dryRun: boolean) {
    switch (this.type) {
      case 'alias':
        return this.aliasService.importAliases(formData, dryRun);
      case 'preferred-product':
        return this.preferredProductService.importPreferredProducts(formData, dryRun);
      default:
        return this.externalReferenceService.importExternalReferences(formData, dryRun);
    }
  }

  // ── Row helpers ───────────────────────────────────────────────────────────

  getBadgeType(row: IImportRow): BadgeType {
    switch (row.status) {
      case 'valid':
        return 'ok';
      case 'warning':
        return 'warning';
      case 'skipped':
        return 'duplicate';
      case 'invalid':
        return 'error';
    }
  }

  getBadgeLabel(row: IImportRow): string {
    switch (row.status) {
      case 'valid':
        return this.translateService.instant('knowledge_base.import.status.ok');
      case 'warning':
        return this.translateService.instant('knowledge_base.import.status.warning');
      case 'skipped':
        return this.translateService.instant('knowledge_base.import.status.duplicate');
      case 'invalid':
        return this.translateService.instant('knowledge_base.import.status.error');
    }
  }

  getRowReason(row: IImportRow): string {
    if (row.reason) {
      return this.translateReason(row.reason, row.field, row.value);
    }
    if (row.warnings?.length) {
      return row.warnings.map((w) => this.translateReason(w)).join(', ');
    }
    return '—';
  }

  private translateReason(reason: string, field?: string, value?: string): string {
    const key = `knowledge_base.import.reason.${reason}`;
    const translatedField = field ? this.translateService.instant(`knowledge_base.import.field_name.${field}`) : field;
    const translated = this.translateService.instant(key, {
      field: translatedField ?? '',
      value: value ?? '',
    });
    // If the key was not found, ngx-translate returns the key itself — fall back to raw reason
    return translated !== key ? translated : reason;
  }

  getDisplaySku(row: IImportRow): string {
    return row.data?.sku ?? '—';
  }

  getDisplayAlias(row: IImportRow): string {
    return row.data?.alias_value ?? '—';
  }

  getDisplayReferenceCode(row: IImportRow): string {
    return row.data?.reference_code ?? '—';
  }

  getDisplayCustomer(row: IImportRow): string {
    return row.data?.customer ?? row.data?.customer_id ?? '—';
  }

  getDisplayProduct(row: IImportRow): string {
    return row.data?.sku ?? row.data?.product_id ?? '—';
  }

  // ── Close ─────────────────────────────────────────────────────────────────

  close(): void {
    this.dialogRef.close(false);
  }
}
